import { useState } from "react";
import type { LoginPageProduct, LoginPageTenant } from "../types";

/** パスワードを忘れたときの流れ。reissue* は PRODUCT_DEV-1238（メールの無い人）。 */
export type ResetFlowStep =
  | "email"
  | "code"
  | "reissueSent"
  | "reissueCode";

/**
 * ログインページの全てのState管理を集約したカスタムフック
 */
export const useLoginState = () => {
  // フォーム入力状態
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  // エラー状態
  const [error, setError] = useState<string | null>(null);
  const [recoverySuggestion, setRecoverySuggestion] = useState<string | null>(
    null,
  );

  // ローディング状態
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // テナント選択状態
  const [tenants, setTenants] = useState<LoginPageTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<LoginPageTenant | null>(
    null,
  );
  const [showTenantSelect, setShowTenantSelect] = useState<boolean>(false);

  // プロダクト選択状態
  const [products, setProducts] = useState<LoginPageProduct[]>([]);
  const [showSelectUI, setShowSelectUI] = useState<boolean>(false);

  // セッション状態
  const [isSessionChecking, setIsSessionCheking] = useState(true);

  // 認証チャレンジ状態
  const [challengeType, setChallengeType] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  // メールコード認証用
  const [emailCode, setEmailCode] = useState("");

  // TOTP セットアップ用
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [totpSecretKey, setTotpSecretKey] = useState<string | null>(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState("");

  // パスキーログインがユーザーによってキャンセルされたか
  const [passkeyLoginCancelled, setPasskeyLoginCancelled] = useState(false);

  // PRODUCT_DEV-396 ログインID入力時に login-options API で取得するパスキー利用可否
  // null = 未確認, true = 利用可能, false = 不可
  const [passkeyAvailable, setPasskeyAvailable] = useState<boolean | null>(null);

  // PRODUCT_DEV-396 ログイン後パスキー登録促進モーダル
  const [showPasskeyPromptModal, setShowPasskeyPromptModal] = useState(false);

  // パスワードリセット用
  const [resetEmail, setResetEmail] = useState("");
  // PRODUCT_DEV-891: Cognito に渡すユーザー名（独自ID を解決した結果）。画面に出す入力値
  // (resetEmail) とは別に持つ。空なら resetEmail を使う（URL の深いリンク経由など）。
  const [resetUsername, setResetUsername] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  // PRODUCT_DEV-1238: メールの無い人の流れ（依頼 → 依頼したあと / コードを入れる）を足した
  const [resetFlowStep, setResetFlowStep] = useState<null | ResetFlowStep>(
    null,
  );
  // PRODUCT_DEV-1238: 管理者から受け取ったログイン用コード（8文字）
  const [reissueCode, setReissueCode] = useState("");

  return {
    // フォーム入力
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,

    // エラー
    error,
    setError,
    recoverySuggestion,
    setRecoverySuggestion,

    // ローディング
    loading,
    setLoading,
    loadingMessage,
    setLoadingMessage,

    // テナント選択
    tenants,
    setTenants,
    selectedTenant,
    setSelectedTenant,
    showTenantSelect,
    setShowTenantSelect,

    // プロダクト選択
    products,
    setProducts,
    showSelectUI,
    setShowSelectUI,

    // セッション
    isSessionChecking,
    setIsSessionCheking,

    // 認証チャレンジ
    challengeType,
    setChallengeType,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    mfaCode,
    setMfaCode,

    // メールコード認証
    emailCode,
    setEmailCode,

    // TOTP セットアップ
    showTOTPSetup,
    setShowTOTPSetup,
    totpUri,
    setTotpUri,
    totpSecretKey,
    setTotpSecretKey,
    totpVerifyCode,
    setTotpVerifyCode,

    // パスキーキャンセル
    passkeyLoginCancelled,
    setPasskeyLoginCancelled,

    // PRODUCT_DEV-396 パスキー利用可否事前判定 / 促進モーダル
    passkeyAvailable,
    setPasskeyAvailable,
    showPasskeyPromptModal,
    setShowPasskeyPromptModal,

    // パスワードリセット
    resetEmail,
    setResetEmail,
    resetUsername,
    setResetUsername,
    resetCode,
    setResetCode,
    resetPassword,
    setResetPassword,
    reissueCode,
    setReissueCode,
    resetConfirmPassword,
    setResetConfirmPassword,
    resetFlowStep,
    setResetFlowStep,
  };
};
